import { useState } from "react";
import { useParams } from "react-router-dom";
import { useUserDetail, useUpdateUser } from "@/api/users";
import { useTenants } from "@/api/tenants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const roleColors: Record<string, "danger" | "accent" | "warning" | "info"> = {
  super_admin: "danger",
  operator: "accent",
  client_admin: "warning",
  client_viewer: "info",
};

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const { data: user, isLoading } = useUserDetail(userId!);
  const { data: tenantsData } = useTenants({ page_size: "100" });
  const updateUser = useUpdateUser(userId!);
  const [selectedTenantId, setSelectedTenantId] = useState("");

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 w-full" /></div>;
  if (!user) return <p className="text-text-muted">User not found.</p>;

  const allTenants = tenantsData?.data ?? [];
  const assignedTenants = allTenants.filter((t) => user.tenant_ids.includes(t.id));
  const availableTenants = allTenants.filter((t) => !user.tenant_ids.includes(t.id));
  const isClientRole = user.role === "client_admin" || user.role === "client_viewer";
  const canAddTenant = isClientRole ? user.tenant_ids.length === 0 : true;

  const handleAddTenant = () => {
    if (!selectedTenantId) return;
    updateUser.mutate(
      { tenant_ids: [...user.tenant_ids, selectedTenantId] },
      { onSuccess: () => setSelectedTenantId("") },
    );
  };

  const handleRemoveTenant = (tenantId: string) => {
    updateUser.mutate({ tenant_ids: user.tenant_ids.filter((id) => id !== tenantId) });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">
            {user.first_name || user.last_name
              ? `${user.first_name} ${user.last_name}`.trim()
              : user.email}
          </h1>
          <Badge variant={roleColors[user.role]}>{user.role.replace("_", " ")}</Badge>
          <Badge variant={user.status === "active" ? "success" : "danger"}>{user.status}</Badge>
        </div>
        <p className="text-sm text-text-muted">{user.email} &middot; Created {new Date(user.created_at).toLocaleDateString()}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>User Info</CardTitle></CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-text-muted">First Name</dt><dd>{user.first_name || "N/A"}</dd></div>
              <div className="flex justify-between"><dt className="text-text-muted">Last Name</dt><dd>{user.last_name || "N/A"}</dd></div>
              <div className="flex justify-between"><dt className="text-text-muted">Email</dt><dd>{user.email}</dd></div>
              <div className="flex justify-between"><dt className="text-text-muted">Role</dt><dd className="capitalize">{user.role.replace("_", " ")}</dd></div>
              <div className="flex justify-between"><dt className="text-text-muted">Status</dt><dd className="capitalize">{user.status}</dd></div>
              <div className="flex justify-between"><dt className="text-text-muted">Created</dt><dd>{new Date(user.created_at).toLocaleString()}</dd></div>
              {user.updated_at && (
                <div className="flex justify-between"><dt className="text-text-muted">Updated</dt><dd>{new Date(user.updated_at).toLocaleString()}</dd></div>
              )}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Assigned Tenants</CardTitle></CardHeader>
          <CardContent>
            {assignedTenants.length > 0 ? (
              <div className="space-y-2">
                {assignedTenants.map((tenant) => (
                  <div key={tenant.id} className="flex items-center justify-between rounded-lg p-2 hover:bg-surface-hover">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{tenant.name}</span>
                      <Badge variant={tenant.status === "active" ? "success" : "warning"}>{tenant.status}</Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveTenant(tenant.id)}
                      disabled={updateUser.isPending}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-muted">No tenants assigned.</p>
            )}

            {canAddTenant && availableTenants.length > 0 && (
              <div className="mt-4 flex gap-2">
                <Select
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  className="flex-1"
                >
                  <option value="">Select a tenant...</option>
                  {availableTenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Select>
                <Button
                  onClick={handleAddTenant}
                  disabled={!selectedTenantId || updateUser.isPending}
                >
                  Add
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
